import type { ReactNode } from 'react';

import { useAuthStore } from '@milestone-portal/context/useAuthStore';
import { useIsRestoring } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * Most of the time, we use indexDB to persist the store data, which is an async storage driver,
 * so we need to wait the store has been hydrated / restored first before we can continue.
 */
export function AsyncStorageGate({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const [hasHydrated, setHasHydrated] = useState(false);
  /**
   * There are some offline handling logic we have to get the query cache in the 'beforeLoad' process.
   * @see https://github.com/TanStack/query/discussions/5867#discussioncomment-6731634
   */
  const hasRestored = !useIsRestoring();

  /**
   * We need to get the API token and put it into the header when the app is mounted.
   *
   * @see https://zustand.docs.pmnd.rs/integrations/persisting-store-data#hydration-and-asynchronous-storages
   * @see https://zustand.docs.pmnd.rs/integrations/persisting-store-data#how-can-i-check-if-my-store-has-been-hydrated
   */
  useEffect(() => {
    const unsubFinishHydration = useAuthStore.persist.onFinishHydration(() =>
      setHasHydrated(true)
    );

    setHasHydrated(useAuthStore.persist.hasHydrated());

    return () => {
      unsubFinishHydration();
    };
  }, []);

  return hasRestored && hasHydrated ? children : fallback;
}
